import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Chip, ProductPicker, type PickerValue } from '@/components/ui'
import { useAcceptSuggestedRows, usePatchIntakeLine } from '../api/queries'
import type { VendorItem, VendorPricelistIntakeLineWire as Line } from '../api/types'
import { lineExplanation, money } from '../utils/price-sheet-review'

export interface PriceSheetLineResolverProps {
  line: Line
  intakeId: string
  vendorName: string
  /** This vendor's live Odoo price rows (product.supplierinfo). */
  vendorItems: VendorItem[]
  /** Odoo row id -> sheet line number already set to update it. */
  claimedBy: ReadonlyMap<number, number>
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

/**
 * Expanded panel under a price-sheet line: says why the line is where it is
 * and offers every fix in place — confirm the suggested Odoo row, pick a
 * different row, add the line as a new tier, map it to another SPI product,
 * or skip it.
 */
export function PriceSheetLineResolver({
  line,
  intakeId,
  vendorName,
  vendorItems,
  claimedBy,
}: PriceSheetLineResolverProps) {
  const patch = usePatchIntakeLine()
  const accept = useAcceptSuggestedRows()
  const [product, setProduct] = useState<PickerValue>(null)

  const suggested = new Set(line.suggested_supplierinfo_ids ?? [])
  const suggestedTmpl = vendorItems.find((i) => suggested.has(i.id))?.product_tmpl_id ?? null
  const tmplId = line.matched_product_tmpl_id ?? suggestedTmpl
  const rows = vendorItems
    .filter((i) => tmplId != null && i.product_tmpl_id === tmplId)
    .sort((a, b) => a.min_qty - b.min_qty || a.id - b.id)
  const current = new Set(
    line.matched_supplierinfo_ids ??
      (line.matched_supplierinfo_id ? [line.matched_supplierinfo_id] : []),
  )
  const done = ['applied', 'scheduled'].includes(line.line_status)
  const busy = patch.isPending || accept.isPending
  const explanation = lineExplanation(line, vendorName)

  function pickRow(rowId: number) {
    patch.mutate(
      { intakeId, lineId: line.id, matched_supplierinfo_id: rowId },
      {
        onSuccess: () => toast.success(`Line ${line.line_no} will update that Odoo row.`),
        onError: (err) => toast.error(errorText(err, 'Could not set the row.')),
      },
    )
  }

  function confirmSuggested() {
    accept.mutate(
      { intakeId, lineIds: [line.id] },
      {
        onSuccess: (res) =>
          res.accepted.length
            ? toast.success(`Line ${line.line_no} confirmed.`)
            : toast.error(res.skipped[0]?.reason ?? 'Nothing confirmed.'),
        onError: (err) => toast.error(errorText(err, 'Could not confirm.')),
      },
    )
  }

  function addAsNewTier(productId: number) {
    patch.mutate(
      { intakeId, lineId: line.id, matched_product_tmpl_id: productId },
      {
        onSuccess: () => {
          setProduct(null)
          toast.success(`Line ${line.line_no} will be added as a new Odoo price row.`)
        },
        onError: (err) => toast.error(errorText(err, 'Could not change the product.')),
      },
    )
  }

  function setStatus(status: 'skipped' | 'unmatched') {
    patch.mutate(
      { intakeId, lineId: line.id, line_status: status },
      { onError: (err) => toast.error(errorText(err, 'Could not update the line.')) },
    )
  }

  const suggestedRows = rows.filter((r) => suggested.has(r.id))
  const canConfirm =
    line.line_status === 'unmatched' &&
    suggestedRows.length > 0 &&
    new Set(suggestedRows.map((r) => r.price)).size === 1

  return (
    <div className="flex flex-col gap-3 border-l-2 border-accent bg-surface-sunk px-4 py-3 text-sm">
      {explanation ? <p className="m-0 max-w-3xl text-secondary">{explanation}</p> : null}

      {canConfirm ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" disabled={busy} onClick={confirmSuggested}>
            Yes, update the suggested row
          </Button>
          <span className="text-xs text-tertiary">
            or choose another row below, add it as a new tier, or skip it.
          </span>
        </div>
      ) : null}

      {tmplId != null ? (
        <div className="flex flex-col gap-1">
          <span className="text-role-section-label">
            {vendorName}&apos;s Odoo prices for {line.matched_product_name ?? 'this product'}
          </span>
          {rows.length === 0 ? (
            <p className="m-0 text-xs text-tertiary">
              {current.size > 0
                ? 'The Odoo row this line matched is not on a product, so it is not listed here.'
                : 'None yet. Add this line as a new tier to create the first one.'}
            </p>
          ) : (
            <table className="w-full max-w-4xl text-sm">
              <thead className="text-role-table-header">
                <tr>
                  <th className="px-2 py-1 text-left">Vendor code</th>
                  <th className="px-2 py-1 text-left">Vendor product name</th>
                  <th className="px-2 py-1 text-right">Min qty</th>
                  <th className="px-2 py-1 text-right">Pack</th>
                  <th className="px-2 py-1 text-right">Odoo price</th>
                  <th className="px-2 py-1 text-left">Valid</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const other = claimedBy.get(r.id)
                  const isCurrent = current.has(r.id)
                  return (
                    <tr key={r.id} className="border-t border-border-subtle">
                      <td className="px-2 py-1 text-secondary">{r.vendor_product_code ?? '—'}</td>
                      <td className="px-2 py-1 text-secondary">{r.vendor_product_name ?? '—'}</td>
                      <td className="px-2 py-1 text-right">
                        {r.min_qty.toLocaleString()} {r.product_uom_name ?? ''}
                      </td>
                      <td className="px-2 py-1 text-right text-tertiary">
                        {r.pack_size ? r.pack_size.toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {money(r.price)}
                        {r.product_uom_name ? `/${r.product_uom_name}` : ''}
                      </td>
                      <td className="px-2 py-1 text-xs text-tertiary">
                        {r.date_start ?? '…'} – {r.date_end ?? 'open'}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <span className="inline-flex items-center gap-2">
                          {suggested.has(r.id) ? (
                            <Chip size="xs" variant="accent">
                              Suggested
                            </Chip>
                          ) : null}
                          {isCurrent ? (
                            <Chip size="xs" variant="success">
                              This line updates it
                            </Chip>
                          ) : other != null && other !== line.line_no ? (
                            <span className="text-xs text-tertiary">Line {other} updates it</span>
                          ) : !done ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={busy}
                              onClick={() => pickRow(r.id)}
                            >
                              Update this row
                            </Button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {!done ? (
        <div className="flex flex-wrap items-center gap-2">
          {tmplId != null && line.line_status !== 'new' ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => addAsNewTier(tmplId)}
            >
              Add as a new tier instead
            </Button>
          ) : null}
          <div className="inline-flex flex-nowrap items-center gap-2">
            <span className="text-xs whitespace-nowrap text-tertiary">
              {tmplId != null ? 'Different SPI product:' : 'Map to an SPI product:'}
            </span>
            <div className="w-[300px] shrink-0">
              <ProductPicker value={product} onChange={setProduct} excludeSamples width={300} />
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || product == null}
              onClick={() => product && addAsNewTier(product[0])}
            >
              Use this product
            </Button>
          </div>
          <span className="ml-auto" />
          {line.line_status === 'skipped' ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setStatus('unmatched')}
            >
              Undo skip
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setStatus('skipped')}>
              Skip this line
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )
}
